import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { topicMeta } from "@/lib/topicMeta";
import { Clock, ThumbsUp, ChevronRight, ChevronLeft, Search, Loader2, FileQuestion } from "lucide-react";

export default function TopicDetailPage() {
  const { slug } = useParams();
  const [topic, setTopic] = useState(null);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/topics/${slug}`)
      .then((r) => setTopic(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading)
    return (
      <div className="py-32 grid place-items-center text-slate-400">
        <Loader2 className="w-7 h-7 animate-spin" />
      </div>
    );

  if (notFound || !topic)
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center">
        <FileQuestion className="w-12 h-12 mx-auto text-slate-300" />
        <h1 className="font-heading text-2xl font-semibold mt-4 text-slate-800">Topic not found</h1>
        <Link to="/" className="text-[#1A73E8] hover:underline mt-3 inline-block">Back to home</Link>
      </div>
    );

  const { Icon, color, bg } = topicMeta(topic.icon_key);
  const articles = topic.articles.filter(
    (a) =>
      a.title.toLowerCase().includes(filter.toLowerCase()) ||
      a.excerpt.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <main data-testid="topic-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <nav className="flex items-center gap-1.5 text-sm text-slate-500 mb-8" data-testid="topic-breadcrumb">
          <Link to="/" className="hover:text-[#1A73E8]">Help Center</Link>
          <ChevronRight className="w-4 h-4 text-slate-300" />
          <span className="text-slate-800 font-medium">{topic.name}</span>
        </nav>

        <div className="flex items-start gap-5">
          <span className="grid place-items-center w-16 h-16 rounded-2xl shrink-0" style={{ backgroundColor: bg, color }}>
            <Icon className="w-8 h-8" />
          </span>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{topic.name}</h1>
            <p className="text-slate-600 mt-2 max-w-2xl">{topic.description}</p>
            <p className="text-sm text-slate-400 mt-2">{topic.article_count} articles</p>
          </div>
        </div>

        <div className="relative mt-8 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid="topic-filter-input"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={`Search in ${topic.name}`}
            className="w-full h-11 pl-11 pr-4 rounded-full bg-white border border-slate-200 text-sm outline-none focus:border-[#1A73E8] focus:ring-4 focus:ring-blue-100 transition-all"
          />
        </div>

        <div className="mt-8 divide-y divide-slate-100 bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {articles.length === 0 && (
            <p className="p-8 text-center text-slate-400 text-sm">No articles match your search.</p>
          )}
          {articles.map((a) => (
            <Link
              key={a.id}
              to={`/article/${a.slug}`}
              data-testid="topic-article-item"
              className="group flex items-center gap-4 px-5 sm:px-6 py-4 hover:bg-slate-50 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-400">{a.section}</div>
                <h4 className="font-medium text-slate-900 group-hover:text-[#1A73E8] transition-colors">{a.title}</h4>
                <p className="text-sm text-slate-500 mt-0.5 line-clamp-1">{a.excerpt}</p>
                <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-400">
                  <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {a.read_time} min</span>
                  <span className="inline-flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5" /> {a.helpful_yes.toLocaleString()}</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 shrink-0" />
            </Link>
          ))}
        </div>

        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#1A73E8] mt-8">
          <ChevronLeft className="w-4 h-4" /> All topics
        </Link>
      </div>
    </main>
  );
}
